import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import { logout } from '../store/slices/authSlice'
import { ReactNode, useState, useEffect, useRef, useLayoutEffect } from 'react'
import {
  BarChartIcon,
  MapIcon,
  UsersIcon,
  SettingsIcon,
  PlusIcon,
  SatelliteIcon,
  StreetIcon,
  MenuIcon,
  CloseIcon,
} from '../assets/icons'
import { LOGO_SVG } from '../assets/images'

interface NavItemProps {
  readonly to?: string
  readonly label: string
  readonly icon: ReactNode
  readonly active: boolean
  readonly onClick?: () => void
  readonly onNavigate?: () => void
}

function NavItem({
  to,
  label,
  icon,
  active,
  onClick,
  onNavigate,
}: NavItemProps) {
  const handleClick = () => {
    if (onClick) {
      onClick()
    }
    // Close mobile menu when navigating
    if (onNavigate) {
      onNavigate()
    }
  }

  const content = (
    <button
      type='button'
      className={`flex items-center w-full gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors cursor-pointer border-none bg-transparent ${
        active ? 'bg-gray-100 text-gray-900' : 'text-gray-600 hover:bg-gray-50'
      }`}
      onClick={handleClick}
    >
      {icon}
      <span>{label}</span>
    </button>
  )

  if (to && to !== '#') {
    return <Link to={to}>{content}</Link>
  }

  return content
}

interface SidebarProps {
  readonly onCreate: () => void
  readonly mapType?: 'street' | 'satellite'
  readonly onMapTypeChange?: (mapType: 'street' | 'satellite') => void
  readonly isMapView?: boolean
  readonly onMobileMenuToggle?: (toggleFn: () => void) => void
}

export default function Sidebar({
  onCreate,
  mapType = 'street',
  onMapTypeChange,
  isMapView = false,
  onMobileMenuToggle,
}: SidebarProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { user } = useAppSelector(state => state.auth)

  // Helper function to check roles
  const hasRole = (roles: string[]): boolean => {
    if (!user) return false
    return roles.some(role => user.role === role.toLowerCase())
  }
  const isActive = (path: string) => location.pathname === path
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsProfileDropdownOpen(false)
      }
    }

    if (isProfileDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isProfileDropdownOpen])

  const handleViewProfile = () => {
    setIsProfileDropdownOpen(false)
    setIsMobileMenuOpen(false)
    navigate('/profile')
  }

  const handleChangePassword = () => {
    setIsProfileDropdownOpen(false)
    setIsMobileMenuOpen(false)
    navigate('/profile', { state: { openChangePassword: true } })
  }

  const handleLogout = async () => {
    setIsProfileDropdownOpen(false)
    setIsMobileMenuOpen(false)
    await dispatch(logout())
    navigate('/login', { replace: true })
  }

  // Role-based visibility
  const isAdmin = hasRole(['admin'])
  // const isUnderwriter = hasRole(['underwriter', 'admin'])
  const canEditZones = hasRole(['underwriter', 'admin'])

  // Expose mobile menu toggle function to parent
  useLayoutEffect(() => {
    if (onMobileMenuToggle) {
      onMobileMenuToggle(() => {
        setIsMobileMenuOpen(prev => !prev)
      })
    }
  }, [onMobileMenuToggle])

  return (
    <>
      {/* Mobile Menu Toggle Button - Show when sidebar is closed, only on mobile (< 640px) and NOT on map view (where Topbar has the menu) */}
      {!isMobileMenuOpen && !isMapView && (
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className='block sm:hidden fixed top-4 left-4 z-[1100] bg-white hover:bg-gray-50 border border-gray-300 rounded-lg shadow-lg p-2 items-center justify-center transition-colors'
          aria-label='Toggle menu'
        >
          <MenuIcon className='w-5 h-5 text-gray-700' />
        </button>
      )}

      {/* Tablet Menu Toggle Button - Show when sidebar is closed, only on tablet (not mobile, not desktop) */}
      {!isMobileMenuOpen && (
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className='hidden sm:flex lg:hidden fixed top-4 left-4 z-[1100] bg-white hover:bg-gray-50 border border-gray-300 rounded-lg shadow-lg p-2 items-center justify-center transition-colors'
          aria-label='Toggle menu'
        >
          <MenuIcon className='w-5 h-5 text-gray-700' />
        </button>
      )}

      {/* Mobile Backdrop */}
      {isMobileMenuOpen && (
        <button
          type='button'
          className='lg:hidden fixed inset-0 bg-black/50 z-[1050] transition-opacity border-none p-0 w-full h-full cursor-default'
          onClick={() => setIsMobileMenuOpen(false)}
          aria-label='Close menu'
          onKeyDown={e => {
            if (e.key === 'Escape') setIsMobileMenuOpen(false)
          }}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 h-full w-[240px] md:w-[250px] lg:w-[280px] min-w-[240px] md:min-w-[250px] lg:min-w-[280px] bg-white p-4 flex flex-col border-r border-gray-200 z-[1100] transform transition-transform duration-300 ease-in-out ${
          isMobileMenuOpen
            ? 'translate-x-0'
            : '-translate-x-full lg:translate-x-0'
        }`}
      >
        {/* Logo / Title */}
        <div className='relative flex items-center gap-3 px-2 py-2 mb-6'>
          {/* Mobile Close Button - Only show when sidebar is open on mobile */}
          {isMobileMenuOpen && (
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className='lg:hidden absolute top-0 right-0 text-gray-400 hover:text-gray-600 z-10 p-2'
              aria-label='Close menu'
            >
              <CloseIcon className='w-6 h-6' />
            </button>
          )}
          {/* Logo */}
          <img
            src={LOGO_SVG}
            alt='Geographical Map Scoring Logo'
            className='w-8 h-8 flex-shrink-0'
          />
          <div className='text-lg font-bold text-gray-900'>GMS</div>
        </div>

        {/* Navigation */}
        <nav className='flex flex-col gap-1 mb-4'>
          {/* Dashboard - All users */}
          <NavItem
            to='/home'
            label='Dashboard'
            icon={<BarChartIcon />}
            active={isActive('/home') || isActive('/')}
            onNavigate={() => setIsMobileMenuOpen(false)}
          />

          {/* Manage Zones - All users can view */}
          <NavItem
            to='/dashboard'
            label='Manage Zones'
            icon={<MapIcon />}
            active={isActive('/dashboard')}
            onNavigate={() => setIsMobileMenuOpen(false)}
          />

          {/* User Management - Admin only */}
          {isAdmin && (
            <NavItem
              to='/admin/users'
              label='User Management'
              icon={<UsersIcon />}
              active={isActive('/admin/users')}
              onNavigate={() => setIsMobileMenuOpen(false)}
            />
          )}

          {/* API Docs / Settings - hide for viewer role */}
          {user?.role !== 'viewer' && (
            <NavItem
              to='/settings'
              label='API Docs'
              icon={<SettingsIcon />}
              active={
                isActive('/settings') || location.pathname.includes('settings')
              }
              onNavigate={() => setIsMobileMenuOpen(false)}
            />
          )}

          {/* Satellite View Toggle - Mobile only, only on map view */}
          {isMapView && onMapTypeChange && (
            <button
              onClick={() => {
                onMapTypeChange(
                  mapType === 'satellite' ? 'street' : 'satellite'
                )
                setIsMobileMenuOpen(false)
              }}
              className='lg:hidden flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors text-gray-600 hover:bg-gray-50'
            >
              {mapType === 'satellite' ? <StreetIcon /> : <SatelliteIcon />}
              <span>
                {mapType === 'satellite' ? 'Street View' : 'Satellite View'}
              </span>
            </button>
          )}
        </nav>

        {/* Create New Zones CTA - Only for Underwriter and Admin */}
        {canEditZones && (
          <button
            onClick={() => {
              // eslint-disable-next-line no-console
              console.log('[Sidebar] Create clicked', {
                pathname: location.pathname,
                isMapView,
                hasOnCreate: !!onCreate,
              })
              // Always navigate to home page first
              if (location.pathname !== '/home' && location.pathname !== '/') {
                // eslint-disable-next-line no-console
                console.log('[Sidebar] Navigating to /home with create action')
                navigate('/home', { state: { action: 'create' } })
              } else {
                // eslint-disable-next-line no-console
                console.log('[Sidebar] Already on home, calling onCreate')
                onCreate()
              }
              setIsMobileMenuOpen(false)
            }}
            className='mt-1 mb-4 w-full bg-[#6F3FF5] hover:bg-[#5d2fd4] text-white font-semibold py-2.5 rounded-lg shadow transition-colors flex items-center justify-center gap-2'
          >
            <PlusIcon />
            <span>Create New Zones</span>
          </button>
        )}

        {/* Spacer */}
        <div className='flex-1' />

        {/* Profile Card with Dropdown */}
        <div className='mt-auto space-y-2 relative' ref={dropdownRef}>
          {/* Profile Card - Clickable */}
          <button
            onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
            className='w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer text-left'
          >
            {/* Profile Avatar with Online Status */}
            <div className='relative'>
              <div className='w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden'>
                {user?.name ? (
                  <span className='text-sm font-semibold text-gray-700'>
                    {user.name.charAt(0).toUpperCase()}
                  </span>
                ) : (
                  <svg
                    className='h-6 w-6 text-gray-400'
                    fill='currentColor'
                    viewBox='0 0 20 20'
                  >
                    <path
                      fillRule='evenodd'
                      d='M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z'
                      clipRule='evenodd'
                    />
                  </svg>
                )}
              </div>
              {/* Green online status dot */}
              <div className='absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white'></div>
            </div>
            <div className='flex-1 min-w-0'>
              <div className='text-sm font-semibold text-gray-900 truncate'>
                {user?.name || 'User'}
              </div>
              <div className='text-xs text-gray-500 truncate'>
                {user?.email || 'user@example.com'}
              </div>
            </div>
            {/* Dropdown Arrow */}
            <svg
              className={`w-4 h-4 text-gray-400 transition-transform ${isProfileDropdownOpen ? 'rotate-180' : ''}`}
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M19 9l-7 7-7-7'
              />
            </svg>
          </button>

          {/* Dropdown Menu */}
          {isProfileDropdownOpen && (
            <div className='absolute bottom-full left-0 right-0 mb-2 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50'>
              {/* View Profile */}
              <button
                onClick={handleViewProfile}
                className='w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-gray-50 transition-colors text-gray-700'
              >
                <svg
                  className='w-5 h-5 text-gray-500'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'
                  />
                </svg>
                <span className='text-sm font-medium'>View profile</span>
              </button>

              {/* Change Password */}
              <button
                onClick={handleChangePassword}
                className='w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-gray-50 transition-colors text-gray-700'
              >
                <svg
                  className='w-5 h-5 text-gray-500'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z'
                  />
                </svg>
                <span className='text-sm font-medium'>Change Password</span>
              </button>

              {/* Logout */}
              <button
                onClick={handleLogout}
                className='w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-gray-50 transition-colors text-gray-700'
              >
                <svg
                  className='w-5 h-5 text-gray-500'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1'
                  />
                </svg>
                <span className='text-sm font-medium'>Logout</span>
              </button>
            </div>
          )}
        </div>
      </aside>
    </>
  )
}
